import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { api } from '../services/api';
import { socket } from '../services/socket';
import { format } from 'date-fns';
import {
  Send,
  User,
  Bot,
  X,
  Check,
  Edit,
  Sparkles,
  UserPlus,
  ArrowLeft,
  MoreVertical,
  CheckCircle,
  Ban,
  RotateCcw,
  Mail,
} from 'lucide-react';
import type { Message } from '../types';
import VisitorInfo from './VisitorInfo';

export default function ConversationDetail() {
  const { t } = useTranslation();
  const {
    selectedConversation,
    messages,
    setMessages,
    addMessage,
    typingUsers,
    setTypingUser,
    aiSuggestions,
    setAISuggestion,
    clearAISuggestion,
    updateConversation,
    setMobileConversationView,
  } = useStore();

  const [loading, setLoading] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendAsEmail, setSendAsEmail] = useState(false);
  const [requestingAI, setRequestingAI] = useState(false);
  const [editingAI, setEditingAI] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState('');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages();
      socket.joinConversation(selectedConversation.id);

      // Don't leave the room - let the socket stay joined to receive real-time updates
      // The socket will automatically leave all rooms when it disconnects
    }
  }, [selectedConversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages[selectedConversation?.id || '']]);

  // Socket event handlers (defined before the useEffect that uses them)
  const handleNewMessage = useCallback((message: Message) => {
    if (message.conversation_id === selectedConversation?.id) {
      addMessage(selectedConversation.id, message);
    }
  }, [selectedConversation?.id, addMessage]);

  const handleAISuggestion = useCallback((data: { message: Message; aiResponse: any }) => {
    if (data.message.conversation_id === selectedConversation?.id) {
      setAISuggestion(selectedConversation.id, data.aiResponse);
    }
  }, [selectedConversation?.id, setAISuggestion]);

  const handleTypingIndicator = useCallback((data: { conversationId: string; senderType: string; isTyping: boolean }) => {
    if (data.conversationId === selectedConversation?.id) {
      const name = data.senderType === 'ai' ? t('conversations.aiAssistant') : t('conversations.customer');
      setTypingUser(data.conversationId, name, data.isTyping);
    }
  }, [selectedConversation?.id, setTypingUser, t]);

  const handleConversationUpdated = useCallback((conversation: any) => {
    if (conversation.id === selectedConversation?.id) {
      updateConversation(conversation.id, conversation);
    }
  }, [selectedConversation?.id, updateConversation]);

  useEffect(() => {
    // Socket event listeners
    socket.onNewMessage(handleNewMessage);
    socket.onAISuggestion(handleAISuggestion);
    socket.onTypingIndicator(handleTypingIndicator);
    socket.onConversationUpdated(handleConversationUpdated);

    return () => {
      socket.offNewMessage(handleNewMessage);
      socket.offAISuggestion(handleAISuggestion);
      socket.offTypingIndicator(handleTypingIndicator);
      socket.offConversationUpdated(handleConversationUpdated);
    };
  }, [handleNewMessage, handleAISuggestion, handleTypingIndicator, handleConversationUpdated]);

  useEffect(() => {
    // Close actions menu when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showActionsMenu]);

  // Get display name - use actual name, or infer from email
  const getDisplayName = () => {
    if (!selectedConversation) return t('conversations.guest');

    const name = selectedConversation.contact?.name || (selectedConversation as any).contact_name;
    const email = selectedConversation.contact?.email || (selectedConversation as any).contact_email;

    // If they have a real name (not "Anonymous"), use it
    if (name && name.toLowerCase() !== 'anonymous') {
      return name;
    }
    // If they have an email, infer name from it
    if (email) {
      const localPart = email.split('@')[0];
      // Replace dots, underscores, hyphens with spaces and title case
      return localPart
        .replace(/[._-]/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());
    }
    // Fallback
    return t('conversations.guest');
  };

  const loadMessages = async () => {
    if (!selectedConversation) return;

    try {
      const data = await api.getMessages(selectedConversation.id);
      setMessages(selectedConversation.id, data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);

    // Send typing indicator
    if (selectedConversation) {
      socket.sendTypingStart(selectedConversation.id);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        socket.sendTypingStop(selectedConversation.id);
      }, 1000);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || !selectedConversation || sending) return;

    const content = messageInput.trim();
    setMessageInput('');
    setSending(true);

    try {
      if (sendAsEmail) {
        // Check if contact has email
        const contactEmail = selectedConversation.contact?.email || (selectedConversation as any).contact_email;
        if (!contactEmail) {
          alert(t('conversations.noEmailError'));
          setMessageInput(content);
          setSending(false);
          return;
        }
        await api.sendMessageAsEmail(selectedConversation.id, content);
      } else {
        await api.sendMessage(selectedConversation.id, content);
      }
      if (selectedConversation) {
        socket.sendTypingStop(selectedConversation.id);
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      alert(error.response?.data?.error || t('conversations.sendFailed'));
      setMessageInput(content);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleTakeover = async () => {
    if (!selectedConversation) return;

    try {
      await api.takeoverConversation(selectedConversation.id);
      socket.takeoverConversation(selectedConversation.id);
    } catch (error) {
      console.error('Failed to takeover conversation:', error);
    }
  };

  const handleRequestAI = async () => {
    if (!selectedConversation) return;

    setRequestingAI(true);
    try {
      const lastCustomerMessage = messages[selectedConversation.id]
        ?.filter((m) => m.sender_type === 'contact')
        .slice(-1)[0];

      if (lastCustomerMessage) {
        const response = await api.requestAIAssist(selectedConversation.id, lastCustomerMessage.content);
        setAISuggestion(selectedConversation.id, response);
      }
    } catch (error) {
      console.error('Failed to request AI assistance:', error);
    } finally {
      setRequestingAI(false);
    }
  };

  const handleApproveAI = async () => {
    if (!selectedConversation) return;
    const suggestion = aiSuggestions[selectedConversation.id];
    if (!suggestion) return;

    try {
      await api.sendMessage(selectedConversation.id, suggestion.answer);
      clearAISuggestion(selectedConversation.id);
    } catch (error) {
      console.error('Failed to approve AI suggestion:', error);
    }
  };

  const handleEditAndSendAI = async () => {
    if (!selectedConversation) return;
    const suggestion = aiSuggestions[selectedConversation.id];
    if (!suggestion || !editedAnswer.trim()) return;

    try {
      await api.sendMessage(selectedConversation.id, editedAnswer);
      clearAISuggestion(selectedConversation.id);
      setEditingAI(false);
      setEditedAnswer('');
    } catch (error) {
      console.error('Failed to send edited AI answer:', error);
    }
  };

  const handleRejectAI = async () => {
    if (!selectedConversation) return;
    clearAISuggestion(selectedConversation.id);
  };

  const handleCloseConversation = async () => {
    if (!selectedConversation) return;

    try {
      await api.closeConversation(selectedConversation.id);
      updateConversation(selectedConversation.id, { ...selectedConversation, status: 'closed' });
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Failed to close conversation:', error);
    }
  };

  const handleReopenConversation = async () => {
    if (!selectedConversation) return;

    try {
      await api.reopenConversation(selectedConversation.id);
      updateConversation(selectedConversation.id, { ...selectedConversation, status: 'open' });
      setShowActionsMenu(false);
    } catch (error) {
      console.error('Failed to reopen conversation:', error);
    }
  };

  const handleBlockContact = async () => {
    if (!selectedConversation) return;
    const contactId = selectedConversation.contact_id || (selectedConversation as any).contact?.id;
    if (!contactId) return;

    try {
      await api.blockContact(contactId, blockReason || undefined);
      updateConversation(selectedConversation.id, { ...selectedConversation, status: 'closed' });
      setShowBlockDialog(false);
      setShowActionsMenu(false);
      setBlockReason('');
      setMobileConversationView(false); // Go back to list on mobile
    } catch (error) {
      console.error('Failed to block contact:', error);
    }
  };

  if (!selectedConversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 text-gray-500">
        <User className="w-16 h-16 mb-4 text-gray-300" />
        <p className="text-lg">{t('conversations.selectConversation')}</p>
      </div>
    );
  }

  const conversationMessages = messages[selectedConversation.id] || [];
  const currentAISuggestion = aiSuggestions[selectedConversation.id];
  const isTyping = typingUsers[selectedConversation.id]?.isTyping;

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center justify-between gap-3">
          {/* Back button for mobile */}
          <button
            onClick={() => setMobileConversationView(false)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-900" />
          </button>

          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">
              {getDisplayName()}
            </h3>
            {selectedConversation.contact?.email && selectedConversation.contact?.name && selectedConversation.contact.name.toLowerCase() !== 'anonymous' && (
              <p className="text-sm text-gray-600 truncate">{selectedConversation.contact.email}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedConversation.handling_mode === 'ai' && (
              <button
                onClick={handleTakeover}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition text-sm font-medium"
                title={t('conversations.takeOver')}
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">{t('conversations.takeOver')}</span>
              </button>
            )}
            <button
              onClick={handleRequestAI}
              disabled={requestingAI}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-900 rounded-full hover:bg-gray-200 transition text-sm font-medium disabled:opacity-50"
              title={requestingAI ? t('conversations.askingAI') : t('conversations.askAI')}
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">{requestingAI ? t('conversations.asking') : t('conversations.askAI')}</span>
            </button>

            {/* Desktop: Show buttons inline */}
            <div className="hidden lg:flex items-center gap-2">
              {selectedConversation.status === 'closed' ? (
                <button
                  onClick={handleReopenConversation}
                  className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-full hover:bg-green-100 transition text-sm font-medium"
                  title={t('conversations.reopenConversation')}
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>{t('conversations.reopen')}</span>
                </button>
              ) : (
                <button
                  onClick={handleCloseConversation}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-900 rounded-full hover:bg-gray-200 transition text-sm font-medium"
                  title={t('conversations.closeConversation')}
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{t('conversations.close')}</span>
                </button>
              )}

              <button
                onClick={() => setShowBlockDialog(true)}
                className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-full hover:bg-red-100 transition text-sm font-medium"
                title={t('conversations.blockContact')}
              >
                <Ban className="w-4 h-4" />
                <span>{t('conversations.block')}</span>
              </button>
            </div>

            {/* Mobile: Actions Menu */}
            <div className="relative lg:hidden" ref={actionsMenuRef}>
              <button
                onClick={() => setShowActionsMenu(!showActionsMenu)}
                className="flex items-center gap-1 px-3 py-2 bg-gray-100 text-gray-900 rounded-full hover:bg-gray-200 transition text-sm font-medium"
                title={t('common.actions')}
              >
                <span className="text-xs">{t('common.actions')}</span>
                <MoreVertical className="w-4 h-4" />
              </button>

              {showActionsMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {selectedConversation.status === 'closed' ? (
                    <button
                      onClick={handleReopenConversation}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm"
                    >
                      <RotateCcw className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900">{t('conversations.reopenConversation')}</span>
                    </button>
                  ) : (
                    <button
                      onClick={handleCloseConversation}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm"
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900">{t('conversations.closeConversation')}</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowBlockDialog(true);
                      setShowActionsMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm border-t border-gray-100"
                  >
                    <Ban className="w-4 h-4 text-red-600" />
                    <span className="text-red-600 font-medium">{t('conversations.blockContact')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Block Contact Dialog */}
      {showBlockDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('conversations.blockContact')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('conversations.blockConfirmation')}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('conversations.blockReason')}
            </label>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder={t('conversations.blockReasonPlaceholder')}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-4"
              rows={3}
            />

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowBlockDialog(false);
                  setBlockReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleBlockContact}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition flex items-center gap-2"
              >
                <Ban className="w-4 h-4" />
                {t('conversations.blockContact')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Context */}
      <VisitorInfo contact={selectedConversation.contact} />

      {/* AI Suggestion Banner */}
      {currentAISuggestion && (
        <div className="p-4 bg-gray-100 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-gray-900 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-gray-900">{t('conversations.aiSuggestedResponse')}</span>
                <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                  currentAISuggestion.confidence >= 85
                    ? 'bg-green-100 text-green-800'
                    : currentAISuggestion.confidence >= 60
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {currentAISuggestion.confidence}% {t('testAI.confidence')}
                </span>
              </div>

              {editingAI ? (
                <div className="space-y-2">
                  <textarea
                    value={editedAnswer}
                    onChange={(e) => setEditedAnswer(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    rows={4}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleEditAndSendAI}
                      className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition text-sm font-medium"
                    >
                      <Send className="w-4 h-4" />
                      {t('conversations.sendEdited')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingAI(false);
                        setEditedAnswer('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition text-sm font-medium"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mb-3">{currentAISuggestion.answer}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleApproveAI}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition text-xs sm:text-sm font-medium"
                    >
                      <Check className="w-4 h-4" />
                      <span className="hidden xs:inline">{t('conversations.approve')} & </span>{t('common.send')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingAI(true);
                        setEditedAnswer(currentAISuggestion.answer);
                      }}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-200 text-gray-900 rounded-full hover:bg-gray-300 transition text-xs sm:text-sm font-medium"
                    >
                      <Edit className="w-4 h-4" />
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={handleRejectAI}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition text-xs sm:text-sm font-medium"
                    >
                      <X className="w-4 h-4" />
                      {t('conversations.reject')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            {t('conversations.loadingMessages')}
          </div>
        ) : conversationMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            {t('conversations.noMessages')}
          </div>
        ) : (
          conversationMessages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[70%] ${
                  message.sender_type === 'user'
                    ? 'bg-black text-white rounded-3xl rounded-br-md'
                    : message.sender_type === 'ai'
                    ? 'bg-gray-100 text-gray-900 rounded-3xl rounded-bl-md'
                    : 'bg-gray-100 text-gray-900 rounded-3xl rounded-bl-md'
                } p-3`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {message.sender_type === 'ai' && <Bot className="w-4 h-4" />}
                  {message.sender_type === 'contact' && <User className="w-4 h-4" />}
                  {message.metadata?.channel === 'email' && (
                    <span title="Sent via email">
                      <Mail className="w-4 h-4" />
                    </span>
                  )}
                  <span className="text-xs font-medium opacity-80">
                    {message.sender_type === 'ai'
                      ? t('conversations.aiAssistant')
                      : message.sender_type === 'user'
                      ? t('conversations.you')
                      : getDisplayName()}
                  </span>
                  <span className="text-xs opacity-60">
                    {format(new Date(message.created_at), 'HH:mm')}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))
        )}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg rounded-bl-none p-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-600">{typingUsers[selectedConversation.id]?.name} {t('conversations.isTyping')}</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-3 sm:p-4 border-t border-gray-200 bg-white flex-shrink-0">
        <form onSubmit={handleSendMessage} className="space-y-2">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={messageInput}
              onChange={handleInputChange}
              placeholder={sendAsEmail ? t('conversations.typeEmailMessage') : t('conversations.typeMessage')}
              className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3 bg-gray-100 border-0 rounded-full focus:ring-2 focus:ring-gray-900 focus:bg-white resize-none text-sm sm:text-base max-h-32"
              rows={1}
              style={{ minHeight: '44px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <button
              type="button"
              onClick={() => setSendAsEmail(!sendAsEmail)}
              className={`px-3 py-3 rounded-full transition flex-shrink-0 ${
                sendAsEmail
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={{ minHeight: '44px', minWidth: '44px' }}
              title={sendAsEmail ? t('conversations.sendAsEmailDisable') : t('conversations.sendAsEmail')}
            >
              <Mail className="w-5 h-5 mx-auto" />
            </button>
            <button
              type="submit"
              disabled={!messageInput.trim() || sending}
              className="px-4 sm:px-6 py-3 bg-black text-white rounded-full hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              <Send className="w-5 h-5 mx-auto" />
            </button>
          </div>
          {sendAsEmail && (
            <p className="text-xs text-blue-600 px-2 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {t('conversations.emailNotice', { email: selectedConversation.contact?.email || (selectedConversation as any).contact_email || t('conversations.theCustomer') })}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
